import React, { useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useTheme } from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import * as S from './BookmarksListModal.styled';
import { BookmarksList } from './BookmarksList';
import { Modal } from '@/components/UI/Modal';
import { debounce } from '@/utils/debounce';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Button } from '@/components/UI/Button/Button';
import { useToastStore } from '@/store/useToastStore';
import { mergeBookmarksResults } from '@/utils/helpers';
import { BOOKMARKS_CHUNK_SIZE } from '@/constants/common';
import { GET_USER_BOOKMARKS } from '@/api/queries/user.queries';
import { GetUserBookmarksResponse } from '@/api/types/user.types';
import {
  ADD_TO_COLLECTION,
  GET_COLLECTION_DATA,
  GetCollectionDataQuery,
} from '@/features/BookmarkCollections/api';
import { Drawer } from '@/components/Drawer/Drawer';
import { CloseIcon } from '@/components/UI/Icons/Icons';

interface BookmarksListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BookmarksListModal = ({
  isOpen,
  onClose,
}: BookmarksListModalProps) => {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const { openToast } = useToastStore();
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedBookmarks, setSelectedBookmarks] = useState<string[]>([]);

  const [addToCollection, { loading: isAdding }] =
    useMutation(ADD_TO_COLLECTION);
  const { data, fetchMore, loading } = useQuery<GetUserBookmarksResponse>(
    GET_USER_BOOKMARKS,
    {
      skip: !isOpen,
      variables: {
        first: BOOKMARKS_CHUNK_SIZE,
      },
    }
  );

  const { refetch: refetchCollection } = useQuery<GetCollectionDataQuery>(
    GET_COLLECTION_DATA,
    {
      skip: !router.query.id,
      fetchPolicy: 'network-only',
      variables: { id: router.query.id, first: BOOKMARKS_CHUNK_SIZE },
    }
  );

  const bookmarks = data?.currentUser?.bookmarks?.nodes ?? [];
  const { endCursor, hasNextPage } =
    data?.currentUser?.bookmarks?.pageInfo ?? {};

  const toggleSelection = (bookmarkId: string) => {
    setSelectedBookmarks(prev =>
      prev.includes(bookmarkId)
        ? prev.filter(id => id !== bookmarkId)
        : [...prev, bookmarkId]
    );
  };

  const getSuccessText = (isMultiple: boolean) => {
    return isMultiple
      ? t('bookmark_collections.add_to_collection_success_multiple')
      : t('bookmark_collections.add_to_collection_success_single');
  };

  const handleAddToCollection = async () => {
    await addToCollection({
      variables: {
        bookmarkCollectionIds: [router.query.id],
        adLegacyIds: selectedBookmarks,
      },
    });
    await refetchCollection();

    onClose();
    openToast({ text: getSuccessText(selectedBookmarks.length > 1) });
  };

  useEffect(() => {
    const listEl = listRef.current;

    if (!listEl) return;

    document.body.style.overflow = 'hidden';

    const handleScroll = debounce(() => {
      const scrollOffset = listEl.scrollHeight - listEl.scrollTop;
      const threshold = listEl.clientHeight + 50;
      const nearBottom = scrollOffset <= threshold;

      if (nearBottom && !loading && hasNextPage) {
        fetchMore({
          variables: { after: endCursor },
          updateQuery: (prev, { fetchMoreResult }) =>
            mergeBookmarksResults(prev, fetchMoreResult),
        });
      }
    }, 100);

    listEl.addEventListener('scroll', handleScroll);

    return () => {
      setSelectedBookmarks([]);
      document.body.style.overflow = '';
      listEl.removeEventListener('scroll', handleScroll);
    };
  }, [fetchMore, loading, hasNextPage, endCursor, isOpen]);

  if (!isDesktop) {
    return (
      <Drawer
        isOpen={isOpen}
        right={{
          title: (
            <CloseIcon
              width={15}
              height={15}
              fill={theme.palette.primary.labels}
            />
          ),
          onClick: onClose,
        }}
        background={theme.palette.system.white}
        title={t('bookmark_collections.selected_bookmarks_title')}
      >
        <BookmarksList
          ref={listRef}
          loading={loading}
          bookmarks={bookmarks}
          selectedBookmarks={selectedBookmarks}
          onToggleSelection={toggleSelection}
        />

        <S.Footer>
          <Button
            isFullWidth
            borderRadius={14}
            appearance="primary"
            isLoading={isAdding}
            onClick={handleAddToCollection}
            disabled={!selectedBookmarks.length || isAdding}
          >
            {t('bookmark_collections.selected_bookmarks_add_to_collection', {
              count: selectedBookmarks.length,
            })}
          </Button>
        </S.Footer>
      </Drawer>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth={810} isScrollLocked>
      <S.Header>
        <S.Title>{t('bookmark_collections.selected_bookmarks_title')}</S.Title>
        <S.CloseButton onClick={onClose}>
          <CloseIcon
            width={15}
            height={15}
            fill={theme.palette.primary.labels}
          />
        </S.CloseButton>
      </S.Header>
      <div>
        <BookmarksList
          ref={listRef}
          loading={loading}
          bookmarks={bookmarks}
          selectedBookmarks={selectedBookmarks}
          onToggleSelection={toggleSelection}
        />
      </div>
      <S.Footer>
        <Button
          isFullWidth
          borderRadius={14}
          appearance="primary"
          isLoading={isAdding}
          onClick={handleAddToCollection}
          disabled={!selectedBookmarks.length || isAdding}
        >
          {t('bookmark_collections.selected_bookmarks_add_to_collection', {
            count: selectedBookmarks.length,
          })}
        </Button>
      </S.Footer>
    </Modal>
  );
};
